import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/PrivacyPolicyPage.css';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="legal-page-wrapper">
      <div className="legal-page-back">
        <Link to="/home" className="legal-page-back-link">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to home
        </Link>
      </div>
      <main className="legal-page-content">
        <h1 className="legal-page-title">Privacy Policy</h1>
        <p className="legal-page-meta">
          <strong>Last updated:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </p>

        <p className="legal-page-intro">
          Welcome to Mends. Your privacy is important to us. This Privacy Policy describes how we collect, use, share, and protect your personal information when you use our platform and services.
        </p>

        <p className="legal-page-intro">
          By continuing to use our services, you accept this Privacy Policy. If you do not agree, please do not use our platform.
        </p>

        <hr className="legal-page-divider" />

        <h2 className="legal-page-h2">1. Information We Collect</h2>
        <div className="legal-page-section">
          <ul className="legal-page-list">
            <li>
              <strong>Account Information:</strong> Name, email address, phone number, and other information required to create and manage your account.
            </li>
            <li>
              <strong>Usage Information:</strong> Data about how you use the platform, including interactions, preferences, and feature usage.
            </li>
            <li>
              <strong>Device Information:</strong> IP address, browser type, operating system, and device identifiers.
            </li>
            <li>
              <strong>Email and Communications Data:</strong> When you connect your email (e.g., via Gmail OAuth), we use the Google Gmail API to access, send, and manage emails on your behalf. We only access data necessary to provide the requested features (sending emails, listing emails, syncing, etc.).
            </li>
            <li>
              <strong>Cookies and Similar Technologies:</strong> We use cookies and similar technologies to improve your experience, personalize content, and maintain session state.
            </li>
          </ul>
        </div>

        <hr className="legal-page-divider" />

        <h2 className="legal-page-h2">2. How We Use Your Information</h2>
        <div className="legal-page-section">
          <ul className="legal-page-list">
            <li>
              <strong>Service Delivery:</strong> To operate, maintain, and provide our platform and features.
            </li>
            <li>
              <strong>Email Integration:</strong> To send emails, list emails, sync your inbox, and manage labels as authorized by you through the Gmail API.
            </li>
            <li>
              <strong>Communications:</strong> To send notifications, updates, and customer support messages.
            </li>
            <li>
              <strong>Personalization:</strong> To adapt the platform to your preferences and settings.
            </li>
            <li>
              <strong>Analytics and Improvements:</strong> To analyze and improve our services, security, and user experience.
            </li>
          </ul>
        </div>

        <hr className="legal-page-divider" />

        <h2 className="legal-page-h2">3. Google API Services Disclosure</h2>
        <p className="legal-page-paragraph">
          This application uses Google APIs, including the Gmail API. Our use and transfer of information received from Google APIs adheres to the{' '}
          <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">Google API Services User Data Policy</a>, including the Limited Use requirements.
        </p>
        <p className="legal-page-paragraph">
          We only request access to the data necessary for the features you use. Email data is used solely to provide email sending, listing, and synchronization features within our platform. We do not use your data for advertising purposes or share it with third parties for their marketing.
        </p>

        <hr className="legal-page-divider" />

        <h2 className="legal-page-h2">4. Information Sharing</h2>
        <div className="legal-page-section">
          <ul className="legal-page-list">
            <li>
              <strong>Trusted Service Providers:</strong> We may share data with third-party service providers who assist in operating our platform, under confidentiality agreements.
            </li>
            <li>
              <strong>Legal Compliance:</strong> When required by law or to protect our rights, safety, or property.
            </li>
            <li>
              <strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.
            </li>
          </ul>
        </div>

        <hr className="legal-page-divider" />

        <h2 className="legal-page-h2">5. Data Security</h2>
        <p className="legal-page-paragraph">
          We implement appropriate technical and organizational measures to protect your information against unauthorized access, alteration, disclosure, or destruction. This includes encryption of sensitive data, secure communication protocols, and access controls.
        </p>

        <hr className="legal-page-divider" />

        <h2 className="legal-page-h2">6. Your Rights</h2>
        <div className="legal-page-section">
          <ul className="legal-page-list">
            <li>
              <strong>Access and Correction:</strong> You may access and update your personal information through your account settings at any time.
            </li>
            <li>
              <strong>Deletion:</strong> You may request deletion of your personal information, subject to legal obligations.
            </li>
            <li>
              <strong>Opt-out:</strong> You may opt out of promotional communications by following the instructions in those messages.
            </li>
            <li>
              <strong>Revoke Access:</strong> You may revoke our access to your Google account at any time through your Google Account settings.
            </li>
          </ul>
        </div>

        <hr className="legal-page-divider" />

        <h2 className="legal-page-h2">7. Changes to This Policy</h2>
        <p className="legal-page-paragraph">
          We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated policy on this page and updating the "Last updated" date. Your continued use of the platform after changes constitutes acceptance of the updated policy.
        </p>

        <hr className="legal-page-divider" />

        <h2 className="legal-page-h2">8. Contact Us</h2>
        <p className="legal-page-paragraph">
          If you have questions or concerns about this Privacy Policy, please contact us through the contact information available on our platform.
        </p>

        <div className="legal-page-footer">
          <Link to="/home" className="legal-page-back-link">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to home
          </Link>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicyPage;
